import { useState, useEffect } from "react";
import { apiFetch } from "../../../context/AuthContext";
import { getAuthHeaders } from "../utils/tokenUtils";

const usePermissions = ({ profile }) => {
  const API_BASE_URL = process.env.REACT_APP_BASE_URL;
  const clientName = profile?.clientName || localStorage.getItem("clientName");
  const workingCompany = profile?.workingCompany || localStorage.getItem("workingCompany");

  const [modulePermissions, setModulePermissions] = useState({
    payrollManagement: true,
    processPayroll: true,
    dataImport: true,
    dataExport: true,
    medicalInsurance: true,
    employeeDetails: true,
    employeeDeductions: true,
    employeeAllowances: true,
    ignoreAttendance: false,
  });
  const [loadingPermissions, setLoadingPermissions] = useState(true);

  const fetchPermissions = async () => {
    try {
      setLoadingPermissions(true);
      const BASE_URL = API_BASE_URL || process.env.REACT_APP_BASE_URL || "";
      const headers = getAuthHeaders(false);

      // Try admin-level resolver first (requires Authorization header and workingCompany)
      try {
        const client =
          workingCompany || localStorage.getItem("workingCompany") || "";
        if (!client) {
          console.warn(
            "No workingCompany (clientName) found — skipping admin-level permissions fetch",
          );
        } else {
          const resp = await apiFetch(
            `${BASE_URL}/api/admin-levels/resolve/permissions?workingCompany=${encodeURIComponent(
              client,
            )}`,
            { method: "GET" },
          );
          if (resp.ok) {
            const json = await resp.json();
            const p = json.data ?? json.permissions ?? json;
            console.log("✓ Admin level permissions from API:", { client, raw: json, processed: p });

            const normalize = (obj) => {
              const defaults = {
                payrollManagement: true,
                processPayroll: true,
                dataImport: true,
                dataExport: true,
                medicalInsurance: true,
                employeeDetails: true,
                employeeDeductions: true,
                employeeAllowances: true,
                ignoreAttendance: false,
              };
              if (!obj) return defaults;
              if (obj.isSuperAdmin || obj.allModulesAllowed) return defaults;

              const modules = Array.isArray(obj.modules) ? obj.modules : [];
              const features = obj.features || {};

              const moduleCandidates = [
                "payroll",
                "payrollManagement",
                "payroll-management",
                "payroll_management",
                "PayrollManagement",
              ];
              let moduleKey = moduleCandidates.find((k) =>
                modules.includes(k),
              );
              if (!moduleKey && features && typeof features === "object") {
                moduleKey = Object.keys(features).find((k) =>
                  moduleCandidates.includes(k),
                );
              }

              let payrollEnabled = false;
              let processEnabled = false;
              let importEnabled = false;
              let exportEnabled = false;

              const PROCESS_FEATURE_ALIASES = ["process", "processPayroll"];

              const IMPORT_FEATURE_ALIASES = [
                "import",
                "dataImport",
                "importPayroll",
                "importAttendance",
                "importAttendanceDeduction",
                "importAttendanceDeductions",
                "importAllowancesDeductions",
                "importAllowanceDeduction",
              ];

              const EXPORT_FEATURE_ALIASES = [
                "export",
                "dataExport",
                "exportPayroll",
                "exportPayrollExcel",
                "exportPayrollPDF",
                "template",
                "export_template",
              ];

              // Granular export keys (registry: exportPayrollExcel /
              // exportPayrollPDF / template). The generic "export" grant keeps
              // enabling the Excel/PDF buttons for legacy admin-level rows.
              const EXPORT_EXCEL_FEATURE_ALIASES = [
                "exportPayrollExcel",
                "exportpayrollexcel",
              ];
              const EXPORT_PDF_FEATURE_ALIASES = [
                "exportPayrollPDF",
                "exportpayrollpdf",
              ];
              const TEMPLATE_FEATURE_ALIASES = [
                "template",
                "export_template",
                "exporttemplate",
              ];

              const VIEW_FEATURE_ALIASES = ["view", "viewPayroll", "viewpayroll"];
              const UNPAY_FEATURE_ALIASES = ["unpayPayroll", "unpaypayroll"];
              const MEDICAL_INSURANCE_FEATURE_ALIASES = ["medicalInsurance"];
              const EMPLOYEE_DETAILS_FEATURE_ALIASES = ["employeeDetails"];
              const EMPLOYEE_DEDUCTIONS_FEATURE_ALIASES = ["employeeDeductions"];
              const EMPLOYEE_ALLOWANCES_FEATURE_ALIASES = ["employeeAllowances", "employeeallowances"];
              const IGNORE_ATTENDANCE_FEATURE_ALIASES = ["ignoreAttendance"];

              const hasAnyFeatureFlag = (entry, aliases) => {
                if (Array.isArray(entry)) {
                  return aliases.some((k) => entry.includes(k));
                }
                if (entry && typeof entry === "object") {
                  return aliases.some((k) => Boolean(entry[k]));
                }
                return false;
              };

              if (moduleKey) {
                payrollEnabled = true;
                const entry = features[moduleKey];
                const featureConfigExists = entry !== undefined && entry !== null;

                console.log("✓ Found module key:", { moduleKey, entry, featureConfigExists });

                processEnabled = hasAnyFeatureFlag(
                  entry,
                  PROCESS_FEATURE_ALIASES,
                );
                importEnabled = hasAnyFeatureFlag(
                  entry,
                  IMPORT_FEATURE_ALIASES,
                );
                exportEnabled = hasAnyFeatureFlag(
                  entry,
                  EXPORT_FEATURE_ALIASES,
                );
                const exportExcelEnabled = hasAnyFeatureFlag(
                  entry,
                  EXPORT_EXCEL_FEATURE_ALIASES,
                );
                const exportPDFEnabled = hasAnyFeatureFlag(
                  entry,
                  EXPORT_PDF_FEATURE_ALIASES,
                );
                const templateEnabled = hasAnyFeatureFlag(
                  entry,
                  TEMPLATE_FEATURE_ALIASES,
                );
                const viewEnabled = hasAnyFeatureFlag(
                  entry,
                  VIEW_FEATURE_ALIASES,
                );
                const unpayEnabled = hasAnyFeatureFlag(
                  entry,
                  UNPAY_FEATURE_ALIASES,
                );
                const medicalInsuranceEnabled = hasAnyFeatureFlag(
                  entry,
                  MEDICAL_INSURANCE_FEATURE_ALIASES,
                );
                const employeeDetailsEnabled = hasAnyFeatureFlag(
                  entry,
                  EMPLOYEE_DETAILS_FEATURE_ALIASES,
                );
                const employeeDeductionsEnabled = hasAnyFeatureFlag(
                  entry,
                  EMPLOYEE_DEDUCTIONS_FEATURE_ALIASES,
                );
                const employeeAllowancesEnabled = hasAnyFeatureFlag(
                  entry,
                  EMPLOYEE_ALLOWANCES_FEATURE_ALIASES,
                );
                const ignoreAttendanceEnabled = hasAnyFeatureFlag(
                  entry,
                  IGNORE_ATTENDANCE_FEATURE_ALIASES,
                );

                // If features are explicitly configured, respect those settings
                // Don't use fallback defaults when features are configured
                return {
                  payrollManagement: payrollEnabled,
                  view: viewEnabled,
                  processPayroll: processEnabled,
                  unpayPayroll: unpayEnabled,
                  dataImport: importEnabled,
                  dataExport: exportEnabled,
                  exportPayrollExcel: exportExcelEnabled,
                  exportPayrollPDF: exportPDFEnabled,
                  template: templateEnabled,
                  medicalInsurance: medicalInsuranceEnabled,
                  employeeDetails: employeeDetailsEnabled,
                  employeeDeductions: employeeDeductionsEnabled,
                  employeeAllowances: employeeAllowancesEnabled,
                  ignoreAttendance: ignoreAttendanceEnabled,
                };
              }

              payrollEnabled = Boolean(
                obj.payrollManagement ?? obj.payroll ?? moduleCandidates.some((m) => modules.includes(m)),
              );
              processEnabled = Boolean(
                obj.processPayroll ?? obj.process ?? false,
              );
              importEnabled = Boolean(
                obj.dataImport ??
                obj.import ??
                obj.importPayroll ??
                obj.importAttendance ??
                obj.importAttendanceDeduction ??
                obj.importAllowancesDeductions ??
                false,
              );
              exportEnabled = Boolean(obj.dataExport ?? obj.export ?? false);
              const medicalInsuranceEnabled = Boolean(obj.medicalInsurance ?? false);
              const employeeDetailsEnabled = Boolean(obj.employeeDetails ?? false);
              const employeeDeductionsEnabled = Boolean(obj.employeeDeductions ?? false);
              const employeeAllowancesEnabled = Boolean(obj.employeeAllowances ?? false);
              const ignoreAttendanceEnabled = Boolean(obj.ignoreAttendance ?? false);

              console.log("✓ Fallback permissions extracted:", {
                payrollEnabled, processEnabled, importEnabled, exportEnabled,
                medicalInsuranceEnabled, employeeDetailsEnabled, employeeDeductionsEnabled, employeeAllowancesEnabled, ignoreAttendanceEnabled
              });

              // Only apply payroll fallback if payroll is enabled AND no specific permissions are set
              if (payrollEnabled && !processEnabled && !importEnabled && !exportEnabled) {
                processEnabled = true;
                importEnabled = true;
                exportEnabled = true;
              }

              return {
                payrollManagement: payrollEnabled,
                processPayroll: processEnabled,
                dataImport: importEnabled,
                dataExport: exportEnabled,
                medicalInsurance: medicalInsuranceEnabled,
                employeeDetails: employeeDetailsEnabled,
                employeeDeductions: employeeDeductionsEnabled,
                employeeAllowances: employeeAllowancesEnabled,
                ignoreAttendance: ignoreAttendanceEnabled,
              };
            };

            const normalized = normalize(p);
            console.log("✓ Normalized permissions:", normalized);
            setModulePermissions(normalized);
            setLoadingPermissions(false);
            return;
          }
        }
      } catch (err) {
        console.warn(
          "admin-level permissions fetch failed, falling back to company config:",
          err,
        );
      }

      // fallback to company configuration
      try {
        const client = clientName || localStorage.getItem("clientName") || "";
        if (!client) {
          setModulePermissions({
            payrollManagement: true,
            processPayroll: true,
            dataImport: true,
            dataExport: true,
            medicalInsurance: true,
            employeeDetails: true,
            employeeDeductions: true,
            employeeAllowances: true,
            ignoreAttendance: false,
          });
          setLoadingPermissions(false);
          return;
        }
        const res2 = await apiFetch(
          `${BASE_URL}/api/configurations/${encodeURIComponent(client)}`,
          { method: "GET" },
        );
        if (res2.ok) {
          const json2 = await res2.json();
          setModulePermissions(
            json2.data || {
              payrollManagement: true,
              processPayroll: true,
              dataImport: true,
              dataExport: true,
              medicalInsurance: true,
              employeeDetails: true,
              employeeDeductions: true,
              employeeAllowances: true,
              ignoreAttendance: false,
            },
          );
          setLoadingPermissions(false);
          return;
        }
      } catch (err2) {
        console.error("Error fetching company configuration fallback:", err2);
      }

      setModulePermissions({
        payrollManagement: true,
        processPayroll: true,
        dataImport: true,
        dataExport: true,
        medicalInsurance: true,
        employeeDetails: true,
        employeeDeductions: true,
        employeeAllowances: true,
        ignoreAttendance: false,
      });
    } catch (err) {
      console.error("Unexpected error fetching permissions:", err);
      setModulePermissions({
        payrollManagement: true,
        processPayroll: true,
        dataImport: true,
        dataExport: true,
        medicalInsurance: true,
        employeeDetails: true,
        employeeDeductions: true,
        employeeAllowances: true,
        ignoreAttendance: false,
      });
    } finally {
      setLoadingPermissions(false);
    }
  };

  useEffect(() => {
    fetchPermissions();
  }, [clientName, workingCompany]);

  useEffect(() => {
    const handleStorage = () => {
      fetchPermissions();
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [clientName, workingCompany]);

  return { modulePermissions, loadingPermissions };
};

export default usePermissions;